import React, { useState, useEffect, useCallback } from 'react';
import { renderMarkdownToHtml } from '../utils/markdownRenderer';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { Button } from '@/components/ui/button';
import {
  Sparkles, Save, Send, Loader2, X, Pencil, Eye,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, Undo, Redo, Minus, Quote, Type, RotateCcw
} from 'lucide-react';

/* ─── md <-> html helpers ─── */
function mdToHtml(md) {
  if (!md) return '<p></p>';
  let html = md
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr>')
    .replace(/^- (.*$)/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*?<\/li>\n?)+/gs, m => `<ul>${m}</ul>`);
  return html.split('\n').map(l => {
    const t = l.trim();
    if (!t) return '';
    if (t.startsWith('<h') || t.startsWith('<ul') || t.startsWith('<ol') || t.startsWith('<hr') || t.startsWith('<li') || t.startsWith('<blockquote')) return t;
    return `<p>${t}</p>`;
  }).filter(Boolean).join('');
}

function htmlToMd(html) {
  if (!html) return '';
  return html
    .replace(/<h2>(.*?)<\/h2>/g, '## $1\n')
    .replace(/<h3>(.*?)<\/h3>/g, '### $1\n')
    .replace(/<strong>(.*?)<\/strong>/g, '**$1**')
    .replace(/<em>(.*?)<\/em>/g, '*$1*')
    .replace(/<u>(.*?)<\/u>/g, '$1')
    .replace(/<li><p>(.*?)<\/p><\/li>/g, '- $1\n')
    .replace(/<li>(.*?)<\/li>/g, '- $1\n')
    .replace(/<\/?ul>/g, '')
    .replace(/<\/?ol>/g, '')
    .replace(/<hr>/g, '---\n')
    .replace(/<blockquote><p>(.*?)<\/p><\/blockquote>/g, '> $1\n')
    .replace(/<br\s*\/?>/g, '\n')
    .replace(/<p>(.*?)<\/p>/g, '$1\n\n')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/* ─── Toolbar Button ─── */
function TB({ onClick, active, children, title, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-7 h-7 rounded flex items-center justify-center transition-all ${
        disabled ? 'opacity-25 cursor-not-allowed' :
        active ? 'bg-teal-600 text-white' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

export default function SummaryModal({
  isOpen,
  onClose,
  summary,
  onSummaryChange,
  onRegenerateClick,
  onSaveDraft,
  onSendToCustomer,
  loading = false,
  generatingSummary = false
}) {
  const [editing, setEditing] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Placeholder.configure({ placeholder: 'Skriv rapporten her...' }),
    ],
    content: mdToHtml(summary || ''),
    onUpdate: ({ editor: e }) => {
      onSummaryChange(htmlToMd(e.getHTML()));
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[350px] px-8 py-6 text-[15px] leading-[1.8] text-slate-800 ' +
          '[&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-3 ' +
          '[&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-teal-800 [&_h3]:mt-5 [&_h3]:mb-2 ' +
          '[&_strong]:font-semibold [&_strong]:text-slate-900 ' +
          '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 ' +
          '[&_li]:my-1 [&_hr]:my-5 [&_hr]:border-slate-200 [&_p]:my-2 ' +
          '[&_blockquote]:border-l-3 [&_blockquote]:border-teal-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-600',
      },
    },
  });

  /* Sync editor content when summary changes externally (e.g. after AI regeneration) */
  useEffect(() => {
    if (editor && summary !== undefined) {
      const cur = htmlToMd(editor.getHTML());
      if (cur !== summary) {
        editor.commands.setContent(mdToHtml(summary || ''));
      }
    }
  }, [summary, editor]);

  /* Reset editing state when modal opens */
  useEffect(() => {
    if (isOpen) setEditing(false);
  }, [isOpen]);

  const startEditing = useCallback(() => {
    setEditing(true);
    setTimeout(() => editor?.commands.focus('end'), 50);
  }, [editor]);

  const stopEditing = useCallback(() => {
    setEditing(false);
  }, []);

  if (!isOpen) return null;

  const hasSummary = summary && summary.trim().length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col border border-slate-200/60"
        onClick={e => e.stopPropagation()}
        data-testid="summary-modal"
      >
        {/* ─── Header ─── */}
        <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-md shadow-teal-500/20">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900" data-testid="report-modal-title">Laboratorierapport</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {generatingSummary ? 'AI genererer rapport...' : editing ? 'Redigeringsmodus' : 'AI-generert rapport'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="report-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ─── Generating State ─── */}
        {generatingSummary && (
          <div className="flex-1 flex flex-col items-center justify-center py-24">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-lg shadow-teal-500/25 animate-pulse">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mt-6">AI skriv rapport...</h3>
            <p className="text-sm text-slate-500 mt-1">Analyserer prøvedata basert på biologens rapportstil</p>
            <div className="mt-6 w-48 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-teal-500 to-teal-600 rounded-full"
                style={{ width: '60%', animation: 'pulse 1.5s ease-in-out infinite' }}
              />
            </div>
          </div>
        )}

        {/* ─── Content ─── */}
        {!generatingSummary && (
          <>
            {/* Action bar */}
            <div className="px-8 pt-4 pb-2 flex items-center justify-between shrink-0">
              {!editing ? (
                <button
                  onClick={startEditing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-all"
                  data-testid="report-edit-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Rediger rapport
                </button>
              ) : (
                <button
                  onClick={stopEditing}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-teal-700 bg-teal-50 border border-teal-200 hover:bg-teal-100 transition-all"
                  data-testid="report-preview-btn"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Vis forhåndsvisning
                </button>
              )}
              <button
                onClick={onRegenerateClick}
                disabled={generatingSummary}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                data-testid="report-regenerate-btn"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Generer på nytt
              </button>
            </div>

            {/* Editor toolbar — only visible in edit mode */}
            {editing && editor && (
              <div className="mx-8 flex items-center gap-0.5 px-3 py-2 border border-slate-200 border-b-0 rounded-t-xl bg-slate-50/80">
                <TB onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Fet">
                  <Bold className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Kursiv">
                  <Italic className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Understrek">
                  <UnderlineIcon className="w-3.5 h-3.5" />
                </TB>
                <div className="w-px h-4 bg-slate-200 mx-1" />
                <TB onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Overskrift">
                  <Heading2 className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Underoverskrift">
                  <Heading3 className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph') && !editor.isActive('heading')} title="Brødtekst">
                  <Type className="w-3.5 h-3.5" />
                </TB>
                <div className="w-px h-4 bg-slate-200 mx-1" />
                <TB onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Punktliste">
                  <List className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Nummerert liste">
                  <ListOrdered className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Sitat">
                  <Quote className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Skillelinje">
                  <Minus className="w-3.5 h-3.5" />
                </TB>
                <div className="flex-1" />
                <TB onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Angre">
                  <Undo className="w-3.5 h-3.5" />
                </TB>
                <TB onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Gjenta">
                  <Redo className="w-3.5 h-3.5" />
                </TB>
              </div>
            )}

            {/* Main content area */}
            <div
              className="flex-1 overflow-y-auto"
              style={{ maxHeight: editing ? 'calc(92vh - 280px)' : 'calc(92vh - 220px)' }}
            >
              {editing ? (
                /* TipTap RTF editor */
                <div className="mx-8 mb-4 border border-slate-200 rounded-b-xl overflow-hidden bg-white">
                  <EditorContent editor={editor} />
                </div>
              ) : (
                /* Read-only rendered view */
                <div className="px-8 pb-6">
                  {hasSummary ? (
                    <div
                      className="prose prose-slate prose-sm max-w-none leading-[1.8] text-[15px]
                        [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-6 [&_h2]:mb-3
                        [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-teal-800 [&_h3]:mt-5 [&_h3]:mb-2
                        [&_strong]:font-semibold [&_strong]:text-slate-900
                        [&_p]:my-2 [&_p]:text-slate-700
                        [&_ul]:list-disc [&_ul]:pl-6 [&_li]:my-1
                        [&_hr]:my-5 [&_hr]:border-slate-200"
                      dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(summary) }}
                      data-testid="report-preview-content"
                    />
                  ) : (
                    <div className="text-center py-20 text-slate-400">
                      <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Ingen rapport generert enno</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ─── Footer ─── */}
            <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/60 flex justify-between items-center shrink-0">
              <Button
                onClick={onClose}
                variant="ghost"
                disabled={loading}
                size="sm"
                className="text-slate-500"
                data-testid="report-back-btn"
              >
                Tilbake
              </Button>
              <div className="flex gap-2">
                <Button
                  onClick={onSaveDraft}
                  variant="outline"
                  disabled={loading}
                  size="sm"
                  data-testid="report-save-draft-btn"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  {loading ? 'Lagrar...' : 'Lagre som utkast'}
                </Button>
                <Button
                  onClick={onSendToCustomer}
                  disabled={loading}
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="report-send-btn"
                >
                  {loading ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {loading ? 'Sender...' : 'Send til kunde'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
